import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import useIdleTimer from '../helper/idleTimer';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Button,
} from '@chakra-ui/react';
import { store } from "../redux/store"
import { toast } from 'react-toastify';
import { signOut } from 'aws-amplify/auth';
import { useTranslation } from "react-i18next"


const AppLayout = ({ children }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user_id } = useSelector((state) => state.auth)
    const { t } = useTranslation('common')
    const [isIdle, setIsIdle] = useState(false);
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [remainingIdleTime, setRemainingIdleTime] = useState(0);
    const logoutTimeout = 30 * 60 * 1000; // 30 minutes
    const warningDuration = 30 * 1000; // 30 seconds before logout
    const idleDetectionTimeout = logoutTimeout - warningDuration;
    const cancelRef = React.useRef();

    const handleLogout = useCallback(() => {
        handleSignOut()
        store.dispatch({ type: "RESET" })
        navigate('/');
    }, [dispatch, navigate]);

    async function handleSignOut() {
        try {
            await signOut();
            toast.success(t('successfully_logged_out'))
            // Redirect user to the login page here
        } catch (error) {
            toast.error(t('error_logging_out'))
            console.error(error);
        }
    }

    const onIdle = useCallback(() => {
        if (user_id) {
            setIsIdle(true);
            setShowLogoutWarning(true);
            setRemainingIdleTime(warningDuration / 1000); // Initialize remaining time
        }
    }, [user_id]);

    useIdleTimer({ timeout: idleDetectionTimeout, onIdle: onIdle });

    useEffect(() => {
        let countdownInterval;
        if (showLogoutWarning && remainingIdleTime > 0) {
            countdownInterval = setInterval(() => {
                setRemainingIdleTime((prevTime) => prevTime - 1);
            }, 1000);
        } else if (showLogoutWarning && remainingIdleTime <= 0) {
            // Warning timed out, perform logout
            handleLogout();
            setShowLogoutWarning(false);
            setIsIdle(false);
        }

        return () => clearInterval(countdownInterval);
    }, [showLogoutWarning, remainingIdleTime, handleLogout]);

    const handleContinueSession = useCallback(() => {
        setShowLogoutWarning(false);
        setIsIdle(false);
        // The useIdleTimer will reset its timer on the next user activity
    }, []);

    return (
        <div>
            <main>{children}</main>

            {/* Logout Warning Modal */}
            <AlertDialog
                isOpen={showLogoutWarning}
                leastDestructiveRef={cancelRef}
                onClose={handleContinueSession}
                motionPreset='scale'

            >
                <AlertDialogOverlay>
                    <AlertDialogContent mt={"10rem"}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {t('session_timeout_warning')}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {t('session_timeout_1')} <b>{remainingIdleTime}</b> {t('session_timeout_2')}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={handleContinueSession}>
                                {t('continue_session')}
                            </Button>
                            <Button colorScheme="red" ml={3} onClick={handleLogout}>
                                {t('logout_now')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </div >
    );
};

export default AppLayout;